/*
 * <video-modal>
 * Single-video player inside a native `<dialog class="modal">` (see snippets/video-modal.liquid).
 * Opening (from any `data-modal-open` trigger), closing on Escape / backdrop click, focus return and
 * the scroll lock come from the dialog and modal-dialog.js. This element only:
 *   - mounts the `<template>` video and plays it when the dialog opens
 *   - toggles play/pause (`[data-video-modal-toggle]`)
 *   - releases the video when the dialog closes
 *
 * Frame state for styling: `data-ready` once the video can play, `data-playing` while playing,
 * `--video-modal-progress` (0-1) with the playback position.
 */
if (!customElements.get("video-modal")) {
  class VideoModal extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector("dialog");
      this.frame = this.dialog?.querySelector("[data-video-modal-frame]");
      this.toggleButton = this.frame?.querySelector("[data-video-modal-toggle]");
      if (!this.frame) return;

      this.abortController = new AbortController();
      const { signal } = this.abortController;

      this.toggleButton?.addEventListener("click", this.toggle.bind(this), { signal });
      this.dialog.addEventListener("close", this.unmount.bind(this), { signal });

      // modal-dialog.js opens the dialog, so watch for it instead of owning the triggers
      this.observer = new MutationObserver(() => {
        if (this.dialog.open) this.mount();
      });
      this.observer.observe(this.dialog, { attributes: true, attributeFilter: ["open"] });
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.observer?.disconnect();
      this.unmount();
    }

    mount() {
      if (this.video) return this.play();

      const template = this.frame.querySelector("template");
      const video = template?.content.firstElementChild?.cloneNode(true);
      if (!(video instanceof HTMLVideoElement)) return;

      this.videoController = new AbortController();
      const { signal } = this.videoController;

      video.addEventListener("loadeddata", () => this.frame.setAttribute("data-ready", ""), { signal });
      video.addEventListener("play", () => this.setPlaying(true), { signal });
      video.addEventListener("pause", () => this.setPlaying(false), { signal });
      video.addEventListener("ended", () => this.setPlaying(false), { signal });
      video.addEventListener(
        "timeupdate",
        () => {
          if (video.duration)
            this.frame.style.setProperty("--video-modal-progress", video.currentTime / video.duration);
        },
        { signal },
      );

      video.preload = "auto";
      template.after(video);
      // Starts loading right away; Safari can otherwise defer it despite preload="auto"
      video.load();

      this.video = video;
      this.play();
    }

    /** Removes the video, fully releasing its media pipeline. */
    unmount() {
      const { video } = this;
      if (!video) return;

      this.videoController.abort();
      this.setPlaying(false);

      video.pause();
      video.querySelectorAll("source").forEach((source) => source.remove());
      video.removeAttribute("src");
      video.load();
      video.remove();

      this.frame.removeAttribute("data-ready");
      this.frame.style.removeProperty("--video-modal-progress");
      this.video = null;
    }

    play() {
      // Rejected when the browser blocks unmuted autoplay; the play button stays visible instead
      this.video?.play()?.catch(() => {});
    }

    toggle() {
      if (!this.video) return this.mount();

      if (this.video.paused) this.play();
      else this.video.pause();
    }

    setPlaying(playing) {
      this.frame.toggleAttribute("data-playing", playing);
      if (this.toggleButton)
        this.toggleButton.setAttribute(
          "aria-label",
          playing ? this.toggleButton.dataset.pauseLabel : this.toggleButton.dataset.playLabel,
        );
    }
  }

  customElements.define("video-modal", VideoModal);
}
